export const isCloudinaryUrl = (url) => !!url && url.includes('res.cloudinary') && url.includes('/upload/');
export const isCloudinaryDeliveryUrl = (url) => !!url &&
    url.includes('/image/upload/') &&
    (url.includes('res.cloudinary') || url.includes('cloudfront.net'));
const CLOUDINARY_UPLOAD_MARKER = '/image/upload/';
/**
 * Video posters uploaded via Mux → Cloudinary are stored with a small
 * w_225,h_400 transform. The underlying asset supports full-size delivery
 * when that transform is replaced for grid/display use.
 */
export function upscaleCloudinaryPosterUrl(url) {
    if (!isCloudinaryDeliveryUrl(url)) {
        return url;
    }
    const markerIndex = url.indexOf(CLOUDINARY_UPLOAD_MARKER);
    if (markerIndex === -1) {
        return url;
    }
    const prefix = url.slice(0, markerIndex + CLOUDINARY_UPLOAD_MARKER.length);
    const remainder = url.slice(markerIndex + CLOUDINARY_UPLOAD_MARKER.length);
    const segments = remainder.split('/');
    const versionIndex = segments.findIndex((segment) => /^v\d+$/.test(segment));
    if (versionIndex <= 0) {
        return url;
    }
    const transform = segments[0];
    if (!isCloudinaryTransformSegment(transform)) {
        return url;
    }
    const pathAfterVersion = segments.slice(versionIndex).join('/');
    return `${prefix}c_fill,q_auto:best/${pathAfterVersion}`;
}
export const isValidCloudinaryCrop = (crop) => !!crop && crop.width > 0 && crop.height > 0;
/**
 * A `c_crop,x_,y_,w_,h_` segment as emitted by {@link buildCroppedImageUrl},
 * living in its own transform segment between two `/`s — anywhere in the
 * chain, not just right after `/upload/`, so a URL that appended crop after
 * other transforms (see {@link buildCroppedImageUrl}) still matches. Only
 * matches the shape this module writes — other `c_crop` variants (missing
 * coords, extra params in the same segment) are left alone so we never
 * accidentally clobber author-authored URLs we didn't create. Global so a
 * legacy URL with more than one such segment strips cleanly on round-trip.
 */
const CLOUDINARY_CROP_SEGMENT_REGEX = /\/c_crop,x_(\d+),y_(\d+),w_(\d+),h_(\d+)\//g;
/**
 * Cloudinary transformation parameter flags (the key before `_` in a chained
 * transform). Used to distinguish real transform segments from public_id
 * folders like `en_us` or `img_banner` on versionless URLs — a shape-only
 * regex would misclassify those, spliceing a new transform into the middle
 * of the public id and resolving to the wrong asset.
 *
 * Kept in sync with the backend allowlist in `cloudinary-url.ts`.
 */
const CLOUDINARY_TRANSFORM_PARAMS = new Set('a ac af ar b bo br c co cs d dl dn dpr du e eo f fl fn fps g h if ki l o p pg q r so sp t u vc vs w x y z'.split(' '));
const isCloudinaryTransformPart = (part) => {
    const match = /^([a-z]{1,3})_(.+)$/i.exec(part);
    if (!match)
        return false;
    return CLOUDINARY_TRANSFORM_PARAMS.has(match[1].toLowerCase());
};
/**
 * True for a Cloudinary chained-transform segment (not a public_id path
 * part). Every comma-separated component must use a known Cloudinary param
 * flag, so folders like `en_us` / `img_banner` are left in the public id.
 * Segments with a `.` are treated as path.
 */
export const isCloudinaryTransformSegment = (segment) => {
    if (!segment || segment.includes('.'))
        return false;
    const parts = segment.split(',');
    return parts.length > 0 && parts.every(isCloudinaryTransformPart);
};
export const countLeadingTransformSegments = (segments) => {
    let count = 0;
    while (count < segments.length &&
        isCloudinaryTransformSegment(segments[count])) {
        count++;
    }
    return count;
};
/**
 * Removes a crop segment previously written by {@link buildCroppedImageUrl}
 * so the caller sees the source image again — useful when the crop UI needs
 * the uncropped source to render, and pairs with {@link parseCloudinaryCrop}
 * to recover the crop rectangle. No-op on URLs that don't have one.
 */
export const stripCloudinaryCrop = (url) => url.replace(CLOUDINARY_CROP_SEGMENT_REGEX, '/');
/**
 * Reads a crop rectangle we previously wrote into a Cloudinary URL via
 * {@link buildCroppedImageUrl}. Returns undefined for non-Cloudinary URLs or
 * URLs with no such segment. Round-trips {@link buildCroppedImageUrl}.
 */
export const parseCloudinaryCrop = (url) => {
    if (!isCloudinaryUrl(url))
        return undefined;
    // exec against a fresh regex — the shared one is global and stateful.
    const match = new RegExp(CLOUDINARY_CROP_SEGMENT_REGEX.source).exec(url);
    if (!match)
        return undefined;
    return {
        x: Number(match[1]),
        y: Number(match[2]),
        width: Number(match[3]),
        height: Number(match[4]),
    };
};
/**
 * Inserts `segment` as the LAST transform in a Cloudinary URL — after any
 * existing transform segments and before the version (`v\d+`) or public_id
 * path. Cloudinary evaluates transforms left-to-right, so the new segment
 * sees the output of whatever came before it, matching what the crop UI
 * actually rendered when it loaded the URL. Returns the URL unchanged on
 * shapes it can't parse. The public_id boundary uses the same allowlist as
 * the backend so folders like `en_us` / `img_banner` aren't misread as
 * transforms.
 */
function insertCloudinaryTransform(url, segment) {
    const delimiter = '/upload/';
    const idx = url.indexOf(delimiter);
    if (idx === -1)
        return url;
    const afterUpload = idx + delimiter.length;
    const segments = url.slice(afterUpload).split('/');
    const versionIndex = segments.findIndex((s) => /^v\d+$/.test(s));
    const insertAt = versionIndex === -1
        ? countLeadingTransformSegments(segments)
        : versionIndex;
    segments.splice(insertAt, 0, segment);
    return url.slice(0, afterUpload) + segments.join('/');
}
export const buildCroppedImageUrl = (url, crop) => {
    if (!isValidCloudinaryCrop(crop) || !isCloudinaryUrl(url))
        return url;
    // Idempotent: a re-crop drops the previous segment rather than stacking.
    // The new segment goes at the END of the transform chain — Cloudinary
    // applies transforms left-to-right, so placing crop last means it acts
    // on whatever the earlier transforms produced (e.g. buildFittedImageUrl's
    // c_fill/w_/h_ resize). Prepending would apply crop to the original
    // image instead, and pixel coordinates the modal captured against the
    // resized view would land on the wrong region.
    const cleaned = stripCloudinaryCrop(url);
    const { x, y, width, height } = crop;
    const segment = `c_crop,x_${Math.round(x)},y_${Math.round(y)},w_${Math.round(width)},h_${Math.round(height)}`;
    return insertCloudinaryTransform(cleaned, segment);
};
/**
 * Cloudinary URL that fills the source into an exact width×height box —
 * auto-picking the focal point (`g_auto`) and an efficient format/quality
 * (`f_auto:animated,q_auto`) — without needing the source's own dimensions.
 * Plain `f_auto` flattens an animated WebP/GIF to its first frame, so the
 * `:animated` qualifier is required to keep motion through the resize.
 * Cloudinary ignores `g_auto` on animated sources and centre-crops them
 * instead — a silent downgrade of the focal point, not an error, so the two
 * combine safely. Unlike {@link buildCroppedImageUrl} (a literal pixel crop
 * of a KNOWN-size source, e.g. a user-drawn crop rectangle), this works for a
 * newly picked/uploaded image whose dimensions we've never measured, so it's
 * the right tool for sizing a replacement image into an existing slot.
 */
export const buildFittedImageUrl = (url, width, height) => {
    if (!isCloudinaryUrl(url) || width <= 0 || height <= 0)
        return url;
    const delimiter = '/upload/';
    const idx = url.indexOf(delimiter);
    if (idx === -1)
        return url;
    const base = url.slice(0, idx);
    const path = url.slice(idx + delimiter.length);
    return `${base}/upload/c_fill,g_auto,w_${Math.round(width)},h_${Math.round(height)},f_auto:animated,q_auto/${path}`;
};
/**
 * Default square box for a replacement/added image with no size to match
 * (no existing crop, no existing `<img>` width/height) — high enough for
 * retina mobile screens; `f_auto:animated,q_auto` in
 * {@link buildFittedImageUrl} keeps the served bytes reasonable regardless.
 */
export const DEFAULT_FIT_IMAGE_SIZE = 1200;
export const DEFAULT_SQUARE_ASPECT_RATIO = '1 / 1';
export const getCropAspectRatio = (crop) => {
    if (!isValidCloudinaryCrop(crop)) {
        return DEFAULT_SQUARE_ASPECT_RATIO;
    }
    return `${crop.width} / ${crop.height}`;
};
export const hasAppliedImageCrop = (url, crop) => isValidCloudinaryCrop(crop) && isCloudinaryUrl(url);
export const getAppliedCropAspectRatio = (url, crop) => hasAppliedImageCrop(url, crop)
    ? getCropAspectRatio(crop)
    : DEFAULT_SQUARE_ASPECT_RATIO;
